import { useEffect } from "react";

import type { ValidationTypes } from "../../models/formFields";
import { notEmpty, unique } from "../../utils/functional";
import { runValidator } from "../validation";

/**
 * Hook for managing form field validation
 */
export const useFormValidation = <FieldName extends string>(props: {
    name: FieldName;
    value?: string | number | boolean | null;
    validation?: ValidationTypes[];
    required?: boolean;
    onValidStateChange?: (name: FieldName, errors: string[]) => void;
}): {
    runValidators: () => string[];
    isRequired: boolean;
    getValidators: () => ValidationTypes[];
} => {
    const getValidators = (): ValidationTypes[] => {
        let validators: ValidationTypes[] = [];
        if (props.validation) {
            validators = validators.concat(props.validation);
        }
        // If field is not required and is empty, skip validation
        if (validators.indexOf("required") === -1 && !props.value) {
            return [];
        }
        return validators;
    };

    const runValidators = (): string[] => {
        const validators = getValidators();
        const val = props.value || "";
        // If field is not required and is empty, skip validation
        if (validators.indexOf("required") === -1 && !props.value) {
            return [];
        }
        // Otherwise, run validation
        const errors = validators
            .filter(unique)
            .map((validator) => {
                return runValidator(validator, val.toString().trim());
            })
            .filter(notEmpty);
        if (props.onValidStateChange) {
            props.onValidStateChange(props.name, errors);
        }
        return errors;
    };

    const isRequired = (): boolean => {
        const validators = getValidators();
        return !!props.required || validators.includes("required");
    };

    // Run validators on mount
    useEffect(() => {
        runValidators();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Run validators when value changes
    useEffect(() => {
        runValidators();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.value]);

    // Clear validation state on unmount
    useEffect(() => {
        return () => {
            if (props.onValidStateChange) {
                props.onValidStateChange(props.name, []);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        runValidators,
        isRequired: isRequired(),
        getValidators,
    };
};
